// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    links = "links",
    showSignOutLink = "showSignOutLink",
    showSplitter = "showSplitter",
    customCSS = "customCSS",
    // XNG-Change
    showBorder = "showBorder",
}

const enum linkFields {
    url = "url",
    linkText = "linkText",
    openInNewTab = "openInNewTab",
}

type LinkModel = {
    fields: {
        url: LinkFieldValue;
        linkText: string;
        openInNewTab: boolean;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.showSignOutLink]: boolean;
        [fields.showSplitter]: boolean;
        [fields.customCSS]: string;
        [fields.showBorder]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    isPunchOutSession: getIsPunchOutSession(state),
});

const ListItemStyle = styled.li<InjectableCss & { showMidLinkSplitter?: boolean; }>`
    display: flex;
    list-style: none;
    margin-bottom: 16.5px;
    &:last-child {
        margin-bottom: 0;
    }
    ${({ showMidLinkSplitter }) => showMidLinkSplitter && `margin-bottom: 0;`};
    ${injectCss}
`;
const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};
export interface MyAccountNavigationListStyles {
    separator?: InjectableCss;
    link?: LinkPresentationProps;
    signOutLink?: LinkPresentationProps;
    // XNG-Change
    wrapper?: InjectableCss;
}

export const myAccountNavigationListStyles: MyAccountNavigationListStyles = {};

const styles = myAccountNavigationListStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const MyAccountNavigationList = (props: Props) => {
    const { fields, displayChangeCustomerLink, isPunchOutSession, cancelPunchOut, signOut } = props;

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const customCssWrapper = {
        css: css`
            ${props?.fields?.customCSS}
        `,
    };

    const signOutElement = fields.showSignOutLink ? (
        <Link
            className="signout-clickable"
            data-test-selector="user_signout"
            aria-label="sign out"
            onClick={onSignOutHandler}
            {...styles.signOutLink}
        >
            {translate("Sign Out")}
        </Link>
    ) : null;

    const links = fields?.links?.length > 0 ? useGetLinks(fields.links, o => o.fields.url) : [];

    const filteredLinks = links.filter((link: any) => {
        return !link.excludeFromNavigation && !(link.type === "ChangeCustomerPage" && !displayChangeCustomerLink);
    });

    if (!filteredLinks.length) {
        return signOutElement;
    }

    return (
        <StyledWrapper {...customCssWrapper} {...{ showBorder: fields.showBorder }} {...styles.wrapper}>
            <ul className="navigation-list" data-test-selector="navigationList">
                {filteredLinks.map((link: any, index) => {
                    const fieldLink = fields.links[links.indexOf(link)].fields;
                    const showMidLinkSplitter =
                        fields.showSplitter && index === Math.floor(filteredLinks.length / 2) - 1;

                    return (
                        <React.Fragment key={link.url}>
                            <ListItemStyle className="navigation-list-item" {...{ showMidLinkSplitter }}>
                                <Link
                                    className="navigation-list-link"
                                    data-test-selector={`navigationList-${link.title}`}
                                    href={link.url}
                                    target={fieldLink.openInNewTab ? "_blank" : ""}
                                    {...styles.link}
                                    aria-label={fieldLink.linkText || link.title}
                                >
                                    {fieldLink.linkText || link.title}
                                </Link>
                            </ListItemStyle>
                            
                            {showMidLinkSplitter && <ListItemStyle {...{showMidLinkSplitter}}>
                                <StyledWrapper {...styles.separator} />
                            </ListItemStyle>}
                        </React.Fragment>
                    );
                })}
            </ul>
            {fields.showSignOutLink && <StyledWrapper {...styles.separator}></StyledWrapper>}
            {signOutElement}
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
.signout-clickable {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountNavigationList),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                tab: basicTab,
                getDisplay: (item: HasFields, state) => {
                    const {
                        url: { type, value },
                        linkText,
                    } = item.fields;
                    if (linkText) {
                        return linkText;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                fieldDefinitions: [
                    {
                        name: linkFields.url,
                        editorTemplate: "LinkField",
                        isRequired: true,
                        defaultValue: {
                            type: "Page",
                            value: "",
                        },
                    },
                    {
                        name: linkFields.linkText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        validate: (value, item) => (value.length > 40 ? "max allowed characters is 40" : null),
                    },
                    {
                        name: linkFields.openInNewTab,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.showSignOutLink,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showSplitter,
                displayName: "Show Split In The Middle",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            // XNG-Change
            {
                name: fields.showBorder,
                displayName: "Show Border",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
